import { useEffect, useState } from "react";
import { FolderOpen, Users, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { isFolderPickerSupported, pickFolder, useDownloadFolderName } from "@/lib/downloadFolder";
import { manualUrl } from "@/components/HelpButton";
import { useT } from "@/lib/i18n";

const key = (clubId) => `dlhint-dismissed:${clubId}`;

// Tip voor teamleden van een gedeelde club: kies op deze computer zelf een Downloadmap.
export default function DownloadFolderHint({ clubId, shared }) {
  const t = useT();
  const name = useDownloadFolderName(clubId);
  const [dismissed, setDismissed] = useState(true);
  useEffect(() => { setDismissed(!!localStorage.getItem(key(clubId))); }, [clubId]);
  if (!shared || !isFolderPickerSupported() || dismissed || name) return null;

  const dismiss = () => { localStorage.setItem(key(clubId), new Date().toISOString()); setDismissed(true); };
  const pick = async () => {
    try {
      const chosen = await pickFolder(clubId);
      toast.success(t("Map opgeslagen als {name}").replace("{name}", chosen));
    } catch (e) {
      if (e?.name === "AbortError") return;
      toast.error(e?.name === "SecurityError" || e?.name === "NotAllowedError"
        ? t("Deze functie werkt niet in de preview. Na publicatie werkt het gewoon in Edge/Chrome.")
        : t("Mapkeuze mislukt: {msg}").replace("{msg}", e?.message || e?.name || "onbekend"));
    }
  };

  return (
    <div className="relative border-2 border-foreground bg-accent/30 p-4 pr-10 mb-6 flex flex-wrap items-center gap-x-4 gap-y-3" data-testid="download-folder-hint">
      <Users className="w-5 h-5 shrink-0" />
      <div className="flex-1 min-w-[240px] text-sm">
        <div className="font-display font-black uppercase tracking-tight">{t("Deze club is met je gedeeld")}</div>
        <p className="text-muted-foreground mt-0.5">
          {t("Kies op deze computer een Downloadmap voor de exports van deze club — bijvoorbeeld de gedeelde OneDrive- of SharePoint-map van de club.")}{" "}
          <Link to={manualUrl("downloadmap-team")} className="underline" data-testid="download-folder-hint-manual">{t("Hoe werkt dit?")}</Link>
        </p>
      </div>
      <div className="flex gap-2">
        <button onClick={pick} data-testid="download-folder-hint-pick"
          className="btn-brutal bg-primary text-primary-foreground px-3 py-2 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5">
          <FolderOpen className="w-4 h-4" /> {t("Kies Downloadmap")}
        </button>
        <button onClick={dismiss} data-testid="download-folder-hint-later"
          className="btn-brutal bg-background text-foreground px-3 py-2 font-display uppercase font-bold text-xs">{t("Later")}</button>
      </div>
      <button onClick={dismiss} aria-label={t("Sluiten")} data-testid="download-folder-hint-close" className="absolute top-2 right-2 p-1 hover:bg-muted"><X className="w-4 h-4" /></button>
    </div>
  );
}
